import React, { useState } from 'react';

const STEPS = [
  {
    id: 'capture',
    title: 'Capture every chapter',
    body: 'Add the car once. Specs, mods, and photos stay with the profile as the build evolves.',
    image: '/images/brand/home-build-record-wide.jpg',
    imageAlt: 'Documented build record with parts and photos laid out on a workbench',
    detail: 'Start from a blank garage or import an existing parts list.',
  },
  {
    id: 'share',
    title: 'Share the build',
    body: 'Send a public link or print a QR for the shop, the paddock, or the group chat.',
    image: '/images/brand/home-qr-windshield.jpg',
    imageAlt: 'QR code being placed on a car windshield for sharing the build page',
    detail: 'One page for judges, shops, and friends. No folder hunting.',
  },
  {
    id: 'events',
    title: 'Take it to events',
    body: 'Register from your garage. Awards and history stay on the same profile.',
    image: '/images/brand/paddock-meet-dusk.jpg',
    imageAlt: 'Modified cars gathered at a dusk paddock meet',
    detail: 'Event day becomes another chapter on the car, not a one-off spreadsheet.',
  },
];

/**
 * Visual process section: hover/select a step to preview the moment.
 */
export const HowBiltWorks: React.FC = () => {
  const [active, setActive] = useState(0);
  const step = STEPS[active];

  return (
    <section
      className="py-16 sm:py-24 border-t border-white/[0.06]"
      aria-labelledby="how-story-heading"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-xl mb-10 sm:mb-12">
          <h2
            id="how-story-heading"
            className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-white text-balance"
          >
            How the story gets told
          </h2>
          <p className="text-gray-400 text-sm sm:text-base mt-3 leading-relaxed max-w-[40ch]">
            Three moments. No feed. No caption chasing.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-stretch">
          <div className="lg:col-span-5 relative min-h-[16rem] sm:min-h-[20rem] rounded-2xl overflow-hidden border border-white/[0.08]">
            {STEPS.map((s, i) => (
              <img
                key={s.id}
                src={s.image}
                alt={i === active ? s.imageAlt : ''}
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ease-out ${
                  i === active ? 'opacity-100' : 'opacity-0'
                }`}
                loading={i === 0 ? 'eager' : 'lazy'}
                decoding="async"
              />
            ))}
            <div
              className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/35 to-transparent"
              aria-hidden
            />
            <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
              <p className="font-display text-lg font-semibold text-white tracking-tight mb-1">
                {step.title}
              </p>
              <p className="text-gray-200 text-sm sm:text-base leading-relaxed max-w-[36ch]">
                {step.detail}
              </p>
            </div>
          </div>

          <ol className="lg:col-span-7 flex flex-col gap-2 list-none">
            {STEPS.map((item, index) => {
              const selected = index === active;
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => setActive(index)}
                    onMouseEnter={() => setActive(index)}
                    onFocus={() => setActive(index)}
                    className={`w-full text-left rounded-2xl border px-5 py-4 sm:px-6 sm:py-5 transition-all duration-300 active:scale-[0.995] ${
                      selected
                        ? 'border-orange-500/35 bg-orange-500/[0.07] shadow-[inset_0_1px_0_rgba(249,115,22,0.12)]'
                        : 'border-white/[0.06] bg-[#0c0c0c]/60 hover:border-white/[0.12] hover:bg-[#111]'
                    }`}
                    aria-current={selected ? 'step' : undefined}
                  >
                    <div className="flex gap-4 sm:gap-5">
                      <span
                        className={`font-display text-2xl font-semibold tracking-tight tabular-nums transition-colors ${
                          selected ? 'text-orange-400' : 'text-gray-600'
                        }`}
                        aria-hidden
                      >
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <div className="min-w-0">
                        <h3 className="font-display text-base sm:text-lg font-semibold tracking-tight text-white">
                          {item.title}
                        </h3>
                        <p
                          className={`text-sm leading-relaxed mt-1.5 transition-colors ${
                            selected ? 'text-gray-300' : 'text-gray-500'
                          }`}
                        >
                          {item.body}
                        </p>
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>

      </div>
    </section>
  );
};

export default HowBiltWorks;
